import parse from 's-expression';
import { TCondition, TConditionOperator, TGroup } from './types';

export const SEXPR_OPERATORS: Record<TConditionOperator, string> = {
  EQ: '==',
  NE: '!=',
  GT: '>',
  GTE: '>=',
  LT: '<',
  LTE: '<=',
};

export const toSexp = (
  filter: TGroup | undefined,
  options: FilterOptions
): string => {
  if (!filter) {
    return '';
  }

  const groupSexp = toSexprFilterGroup(filter, options);

  return groupSexp ?? '';
};

const toSexprFilterGroup = (
  group: TGroup,
  options: FilterOptions
): string | undefined => {
  const values = group.values;
  if (!values || !values.length) {
    return undefined;
  }
  const operator = group.operator;
  const sexpOperator = operator === 'AND' ? 'and' : 'or';

  // conditions still being filled in (and groups holding only those) have
  // no expression yet, so they are left out rather than turned into an
  // operator with no operands
  const sexprs = values
    .map((value) =>
      value.type === 'GROUP'
        ? toSexprFilterGroup(value, options)
        : toSexprFilterCondition(value, options)
    )
    .filter((sexpr) => !!sexpr);

  if (!sexprs.length) {
    return undefined;
  }
  return `(${sexpOperator} ${sexprs.join(' ')})`;
};

const toSexprFilterCondition = (
  condition: TCondition,
  options: FilterOptions = {}
): string | undefined => {
  const key = condition.key;
  const operator = condition.operator;
  const values = condition.values;
  if (!key || !values?.length) {
    return undefined;
  }
  // "is not" any of the values means none of them match; every other
  // operator matches when any of the values does
  const valuesUnionOperator = operator === 'NE' ? 'and' : 'or';
  const sexprOperator = SEXPR_OPERATORS[operator];
  const fieldTransformersKeys = Object.keys(options.fieldTransformers ?? {});
  const sexprs = values.map((value) =>
    !!key && fieldTransformersKeys.includes(key!)
      ? options.fieldTransformers![key!](value, operator)
      : `(${sexprOperator} ${key} "${value.filterObject.value}")`
  );

  if (!sexprs || !sexprs.length) {
    return undefined;
  }
  return `(${valuesUnionOperator} ${sexprs.join(' ')})`;
};

const isEmptyValue = (a): boolean =>
  a === null ||
  a === undefined ||
  a === '' ||
  (Array.isArray(a) && a.length === 0);

// numbers and numeric strings compare as numbers; anything else (missing
// values, placeholder strings such as "not yet rated") never satisfies a
// numeric comparison
const toComparableNumber = (value): number => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    return Number(value);
  }
  return NaN;
};

const compareNumbers =
  (compare: (a: number, b: number) => boolean) =>
  (a, b): boolean => {
    const left = toComparableNumber(a);
    const right = toComparableNumber(b);
    return !Number.isNaN(left) && !Number.isNaN(right) && compare(left, right);
  };

const BINARY_OPERATORS = {
  '==': (a, b) => a == b,
  '!=': (a, b) => a != b,
  '>': compareNumbers((a, b) => a > b),
  '>=': compareNumbers((a, b) => a >= b),
  '<': compareNumbers((a, b) => a < b),
  '<=': compareNumbers((a, b) => a <= b),
  in: (a, b) => {
    if (Array.isArray(b)) {
      return b.some((item) => item == a);
    }
    return false;
  },
  'not-in': (a, b) => {
    if (Array.isArray(b)) {
      return !b.some((item) => item == a);
    }
    return true;
  },
};

const UNARY_OPERATORS = {
  not: (a) => !a,
  'is-empty': isEmptyValue,
};

export type FilterOptions = {
  fieldTransformers?: Record<
    string,
    (value: any, operator: TConditionOperator) => string
  >;
  // computes the value of a field from the target instead of reading
  // `target[field]`, e.g. to flatten a nested response into something the
  // operators above can compare against
  fieldResolvers?: Record<string, (target: any) => unknown>;
};

export const toParsedSexpr = (filter, options) => {
  return parse(toSexp(filter, options));
};

// this is not efficient for many rows, use
export const matchesFilter = (
  filter: TGroup | undefined,
  target: object,
  options: FilterOptions = {}
) => {
  return matchesParsed(toParsedSexpr(filter, options), target, options);
};

export const matchesParsed = (
  parsed: string | undefined,
  target: object,
  options: FilterOptions = {}
) => {
  if (!parsed) {
    return true; // If no filter is provided, all targets match
  }
  return evaluateParsed(parsed, target, options);
};

export const evaluate = (
  expr: string,
  target: object,
  options: FilterOptions = {}
) => {
  const parsed = parse(expr);
  return evaluateParsed(parsed, target, options);
};

// Literal/resolved value is marked as String (capital S) to distinguish it from the primitive string type.
// This is important because the parser uses String to represent resolved values, while primitive strings are used
// for keys in the target object. This allows us to differentiate between a resolved value and a
// property to look up types as string.
const extractValue = (value, target, options: FilterOptions = {}) => {
  if (Array.isArray(value)) {
    return evaluateParsed(value, target, options);
  } else if (value instanceof String) {
    return value.toString();
  } else if (typeof value === 'string') {
    if (value === '') {
      return target;
    }
    const resolver = options.fieldResolvers?.[value];
    if (resolver) {
      return resolver(target);
    }
    const dotKeyIdx = value.indexOf('.');
    const openArrayKeyIdx = value.indexOf('[');
    if (
      dotKeyIdx > -1 &&
      (openArrayKeyIdx === -1 || dotKeyIdx < openArrayKeyIdx)
    ) {
      return extractSubObject(value, target, options);
    } else if (
      openArrayKeyIdx > -1 &&
      (dotKeyIdx === -1 || openArrayKeyIdx < dotKeyIdx)
    ) {
      return extractArrayItem(value, target, options);
    }

    return extractSimpleValue(value, target, options);
  }
  throw new Error('not implemented');
};

const extractSimpleValue = (value, target, _options: FilterOptions = {}) => {
  return target?.[value];
};

const extractSubObject = (value, target, options: FilterOptions = {}) => {
  const dotKeyIdx = value.indexOf('.');
  return extractValue(
    value.slice(dotKeyIdx + 1),
    target?.[value.slice(0, dotKeyIdx)],
    options
  );
};

const extractArrayItem = (value, target, options: FilterOptions = {}) => {
  const openArrayKeyIdx = value.indexOf('[');
  const closeArrayKeyIdx = value.indexOf(']');
  if (closeArrayKeyIdx === -1) {
    throw new Error('Unclosed array bracket');
  }
  const targetIdx = Number(value.slice(openArrayKeyIdx + 1, closeArrayKeyIdx));

  return extractValue(
    value.slice(closeArrayKeyIdx + 1),
    openArrayKeyIdx > 0
      ? target?.[value.slice(0, openArrayKeyIdx)]?.[targetIdx]
      : // Case for root-level or recursive array access i.e. [1] or element[1][2]
        target?.[targetIdx],
    options
  );
};

const evaluateParsed = (expr, target: object, options: FilterOptions = {}) => {
  if (Array.isArray(expr)) {
    const operator = expr[0];
    switch (operator) {
      case 'and':
        return expr
          .slice(1)
          .every((subExpr) => evaluateParsed(subExpr, target, options));
      case 'or':
        return expr
          .slice(1)
          .some((subExpr) => evaluateParsed(subExpr, target, options));
    }
    if (operator in UNARY_OPERATORS) {
      const operatorFunction = UNARY_OPERATORS[operator];
      const operand = extractValue(expr[1], target, options);
      return operatorFunction(operand);
    }
    if (operator in BINARY_OPERATORS) {
      const operatorFunction = BINARY_OPERATORS[operator];
      const left = extractValue(expr[1], target, options);
      const right = extractValue(expr[2], target, options);
      return operatorFunction(left, right);
    }
    throw new Error(`Operator ${operator} not implemented`);
  }

  return expr;
};
